import React, { useState, useCallback, useMemo } from 'react';
import {
  FiTrash2, FiCheckCircle, FiDownload, FiAlertTriangle, FiLoader, FiMail, FiChevronDown, FiCheckSquare
} from 'react-icons/fi';
import { Button } from './ui/Button';
import { useBatchOperations } from '../hooks/useBatchOperations';
import { useBatchSelection } from '../contexts/BatchSelectionContext';
import { useAuth } from '../contexts/AuthContext';
import { BatchJobProgress } from './BatchJobProgress';
import { useApiClient } from '../utils/apiClient';
import { useCategories } from '../hooks/useCategories';
import { useItems } from '../hooks/useItems';
console.log('[InlineBatchActions] BatchJobProgress imported:', BatchJobProgress);

interface InlineBatchActionsProps {
  entityType: 'invoices' | 'customers' | 'items';
  onActionComplete?: () => void;
  onError?: (error: string) => void;
}

export const InlineBatchActions: React.FC<InlineBatchActionsProps> = ({
  entityType,
  onActionComplete,
  onError
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [currentBatchId, setCurrentBatchId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<string | null>(null);
  const [confirmItemCount, setConfirmItemCount] = useState(0);
  const [showEnvelopePrompt, setShowEnvelopePrompt] = useState(false);
  const [includeEnvelopes, setIncludeEnvelopes] = useState(true);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [categoryError, setCategoryError] = useState('');
  const [categoryNotice, setCategoryNotice] = useState('');
  const [isApplyingCategory, setIsApplyingCategory] = useState(false);
  
  // console.log('[InlineBatchActions] Component render - showProgress:', showProgress, 'currentBatchId:', currentBatchId, 'confirmAction:', confirmAction);

  const { executeBatchAction } = useBatchOperations();
  const { selectedItems, getSelectedCount, deselectAll, selectAll, addToSelection } = useBatchSelection();
  const { currentBusiness } = useAuth();
  const { get } = useApiClient();
  const { categories } = useCategories('item');
  const { batchAssignCategory } = useItems();

  const getEntityLabel = (type: string) => {
    switch (type) {
      case 'invoices': return 'Invoices';
      case 'customers': return 'Customers';
      case 'items': return 'Items';
      default: return 'Items';
    }
  };

  const getActions = () => {
    switch (entityType) {
      case 'invoices':
        return [
          { key: 'export_pdf', label: 'Export PDF', icon: FiDownload, color: 'primary' },
          { key: 'email', label: 'Email', icon: FiMail, color: 'primary' },
          { key: 'mark_paid', label: 'Mark as Paid', icon: FiCheckCircle, color: 'primary' },
          { key: 'delete', label: 'Delete', icon: FiTrash2, color: 'danger' }
        ];
      case 'customers':
        return [
          { key: 'export_csv', label: 'Export CSV', icon: FiDownload, color: 'primary' },
          { key: 'delete', label: 'Delete', icon: FiTrash2, color: 'danger' }
        ];
      case 'items':
        return [
          { key: 'edit_category', label: 'Edit Category', icon: FiCheckSquare, color: 'primary' },
          { key: 'export_csv', label: 'Export CSV', icon: FiDownload, color: 'primary' },
          { key: 'delete', label: 'Delete', icon: FiTrash2, color: 'danger' }
        ];
      default:
        return [];
    }
  };

  const handleAction = useCallback(async (action: string) => {
    if (selectedItems.size === 0) return;

    setIsMenuOpen(false);
    if (entityType === 'items' && action === 'edit_category') {
      setCategoryError('');
      setCategoryNotice('');
      setShowEditCategoryModal(true);
      return;
    }
    // For invoice PDF export, prompt for envelopes instead of generic confirm
    if (entityType === 'invoices' && action === 'export_pdf') {
      setShowEnvelopePrompt(true);
      return;
    }
    setConfirmAction(action);
    setConfirmItemCount(selectedItems.size);
  }, [selectedItems]);

  const handleConfirmAction = useCallback(async () => {
    if (!confirmAction || selectedItems.size === 0) return;

    setIsProcessing(true);
    setConfirmAction(null);

    try {
      console.log('[InlineBatchActions] Executing batch action:', confirmAction);
      const result = await executeBatchAction(confirmAction, Array.from(selectedItems), entityType);
      console.log('[InlineBatchActions] Batch action result:', result);
      setCurrentBatchId(result.batchId);
      setShowProgress(true);
      console.log('[InlineBatchActions] Setting showProgress to true, currentBatchId:', result.batchId);
      // Don't deselectAll() here - it will unmount the component before progress dialog shows
      // deselectAll();
      onActionComplete?.();
    } catch (error: any) {
      console.error('Batch action failed:', error);
      onError?.(error.error || 'Batch operation failed');
    } finally {
      setIsProcessing(false);
    }
  }, [confirmAction, selectedItems, executeBatchAction, entityType, deselectAll, onActionComplete, onError]);

  const handleCancelAction = useCallback(() => {
    setConfirmAction(null);
  }, []);

  const handleApplyCategory = useCallback(async () => {
    if (selectedItems.size === 0) return;
    if (!selectedCategoryId) {
      setCategoryError('Please select a category first.');
      return;
    }

    setIsApplyingCategory(true);
    setCategoryError('');
    try {
      const updatedCount = await batchAssignCategory(Array.from(selectedItems), selectedCategoryId);
      setCategoryNotice(`Updated ${updatedCount} item${updatedCount === 1 ? '' : 's'} successfully.`);
      setShowEditCategoryModal(false);
      setSelectedCategoryId('');
      deselectAll();
      onActionComplete?.();
    } catch (error: any) {
      setCategoryError(error?.message || 'Failed to update item categories.');
    } finally {
      setIsApplyingCategory(false);
    }
  }, [selectedItems, selectedCategoryId, batchAssignCategory, deselectAll, onActionComplete]);

  // Start export after user chooses envelope option
  const startExportWithEnvelopeChoice = useCallback(async () => {
    if (selectedItems.size === 0) return;
    try {
      setIsProcessing(true);
      const result = await executeBatchAction('export_pdf', Array.from(selectedItems), entityType, { includeEnvelopes });
      setCurrentBatchId(result.batchId);
      setShowProgress(true);
      setShowEnvelopePrompt(false);
      onActionComplete?.();
    } catch (error: any) {
      console.error('Batch export failed:', error);
      onError?.(error.error || 'Batch export failed');
    } finally {
      setIsProcessing(false);
    }
  }, [selectedItems, executeBatchAction, entityType, includeEnvelopes, onActionComplete, onError]);

  // Function to fetch all IDs for the current entity type
  const fetchAllIds = useCallback(async () => {
    try {
      if (!currentBusiness) {
        throw new Error('No business selected');
      }
      const allIds = await get<string[]>(`/${entityType}/ids`);
      return allIds;
    } catch (error) {
      console.error('Error fetching all IDs:', error);
      throw error;
    }
  }, [entityType, currentBusiness, get]);

  // Function to select all pages
  const handleSelectAllPages = useCallback(async () => {
    try {
      const allIds = await fetchAllIds();
      addToSelection(allIds);
      setIsMenuOpen(false);
    } catch (error) {
      console.error('Error selecting all pages:', error);
      onError?.('Failed to select all pages');
    }
  }, [fetchAllIds, addToSelection, onError]);

  const handleProgressClose = useCallback(() => {
    setShowProgress(false);
    setCurrentBatchId(null);
    // Deselect items when user manually closes the dialog
    deselectAll();
  }, [deselectAll]);

  const handleProgressComplete = useCallback((resultUrl?: string) => {
    console.log('[InlineBatchActions] Progress complete, resultUrl:', resultUrl);
    // Don't auto-download - let user see the progress dialog and choose to download
    // The dialog will stay open and show the download button
    // Only deselect items when user manually closes the dialog
  }, [deselectAll, currentBatchId]);

  if (selectedItems.size === 0) return null;

  return (
    <div className="relative">
      {/* Main Button */}
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        disabled={isProcessing}
        className="flex items-center space-x-2"
      >
        <span>{getSelectedCount()} {getEntityLabel(entityType)} selected</span>
        <FiChevronDown className={`w-4 h-4 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
      </Button>
      {categoryNotice && (
        <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200">
          {categoryNotice}
        </div>
      )}

      {/* Dropdown Menu */}
      {isMenuOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 min-w-48 z-50">
          {/* Header */}
          <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Batch Actions
            </span>
          </div>

          {/* Actions */}
          <div className="py-1">
            {/* Select All Pages Button */}
            <button
              onClick={handleSelectAllPages}
              disabled={isProcessing}
              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 border-b border-gray-200 dark:border-gray-700"
            >
              <FiCheckSquare className="w-4 h-4" />
              <span>Select All Pages</span>
            </button>
            
            {/* Batch Actions */}
            {getActions().map((action) => (
              <button
                key={action.key}
                onClick={() => handleAction(action.key)}
                disabled={isProcessing}
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <action.icon className="w-4 h-4" />
                <span>{action.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <FiAlertTriangle className="w-6 h-6 text-yellow-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Confirm Action
              </h3>
            </div>
            
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Are you sure you want to {confirmAction.replace('_', ' ')} {confirmItemCount} {getEntityLabel(entityType).toLowerCase()}?
            </p>
            
            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleCancelAction}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleConfirmAction}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <FiLoader className="w-4 h-4 animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  'Confirm'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Progress Dialog */}
      {showProgress && currentBatchId && (
        <BatchJobProgress
          batchId={currentBatchId}
          onClose={handleProgressClose}
          onComplete={handleProgressComplete}
        />
      )}

      {/* Envelope choice dialog for invoice PDF export */}
      {showEnvelopePrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h4 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">Generate envelopes too?</h4>
            <div className="text-sm text-gray-600 dark:text-gray-300 mb-3">Default is Yes. You can download envelopes after export completes.</div>
            <div className="flex items-center space-x-3 mb-4">
              <label className="flex items-center space-x-2">
                <input type="checkbox" checked={includeEnvelopes} onChange={(e) => setIncludeEnvelopes(e.target.checked)} />
                <span>Include envelopes</span>
              </label>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="secondary" size="sm" onClick={() => { setShowEnvelopePrompt(false); setIncludeEnvelopes(true); }} disabled={isProcessing}>Cancel</Button>
              <Button size="sm" onClick={startExportWithEnvelopeChoice} disabled={isProcessing}>
                {isProcessing ? 'Starting…' : 'Start Export'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit category dialog for item batch actions */}
      {showEditCategoryModal && entityType === 'items' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h4 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">Edit Category</h4>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Assign one existing category to {selectedItems.size} selected item{selectedItems.size === 1 ? '' : 's'}.
            </p>
            <label htmlFor="batch-item-category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Category
            </label>
            <select
              id="batch-item-category"
              value={selectedCategoryId}
              onChange={(e) => setSelectedCategoryId(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100"
            >
              <option value="">Select a category</option>
              {categories.map((category: any) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            {categoryError && (
              <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-200">
                {categoryError}
              </div>
            )}
            <div className="flex justify-end space-x-2 mt-5">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setShowEditCategoryModal(false);
                  setSelectedCategoryId('');
                  setCategoryError('');
                }}
                disabled={isApplyingCategory}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={handleApplyCategory} disabled={isApplyingCategory}>
                {isApplyingCategory ? 'Applying…' : 'Apply Category'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 